import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Banknote,
  TrendingUp,
  TrendingDown,
  PieChart,
  BarChart3,
  Calendar,
  AlertCircle,
  CheckCircle,
  Clock,
  CreditCard,
  Wallet,
  Building,
  FileText,
  Target,
  Activity,
  X,
} from "lucide-react";
import api from "@/services/api";
import financeService from "@/services/financeService";
import purchaseRequestService from "@/services/purchaseRequestService";

const FinanceDashboard = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState({
    metrics: {
      totalAssets: 0,
      totalLiabilities: 0,
      netWorth: 0,
      totalRevenue: 0,
      totalExpenses: 0,
      netIncome: 0,
      cashBalance: 0,
      pendingTransactions: 0,
    },
    recentTransactions: [],
    budgetData: [],
    period: { type: "month" },
  });
  const [hasData, setHasData] = useState(false);
  // const [subsidiaries, setSubsidiaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState("quarter"); // Changed to 3 months
  const [selectedSubsidiary, setSelectedSubsidiary] = useState("");
  const [receivablesSummary, setReceivablesSummary] = useState(null);
  const [allRequests, setAllRequests] = useState([]);
  const [allTransactions, setAllTransactions] = useState([]);
  const [showExpensesModal, setShowExpensesModal] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    from: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
    to: new Date().toISOString().split('T')[0]
  });

  // useEffect(() => {
  //   fetchSubsidiaries();
  // }, []);

  const SUBSIDIARIES = [
    { value: "", label: "All Subsidiaries" },
    { value: "puffin_group", label: "Puffin Group Ltd" },
    { value: "buildwise", label: "Buildwise Construction Ltd" },
    { value: "laser_shield", label: "Laser Shield Ltd." },
    { value: "maz_logistics", label: "Maz Logistics Ltd." },
    { value: "puffin_energy", label: "Puffin Energy Ltd." },
    { value: "swingtide", label: "Swingtide Consulting Ltd" },
    { value: "puffin_agro", label: "Puffin Agro Allied" },
  ];

  useEffect(() => {
    fetchDashboardData();
    fetchReceivablesAndRequests();
  }, [selectedPeriod, selectedSubsidiary]);

  const fetchReceivablesAndRequests = async () => {
    try {
      setDataLoading(true);
      console.log('🔄 Finance Dashboard: Fetching data...');
      
      // Fetch receivables with payment history
      try {
        const receivablesRes = await financeService.getReceivables({});
        console.log('📊 Receivables Response:', receivablesRes);
        if (receivablesRes?.data?.success) {
          const receivablesData = receivablesRes.data.data.receivables || [];
          console.log('📋 Receivables Data:', receivablesData);
          
          // Fetch payment history for each receivable
          const receivablesWithPayments = await Promise.all(
            receivablesData.map(async (receivable) => {
              try {
                const paymentsResponse = await financeService.getReceivablePaymentHistory(receivable.id);
                const payments = paymentsResponse.data.success ? paymentsResponse.data.data.payments || [] : [];
                console.log(`💰 Receivable ${receivable.invoice_number} has ${payments.length} payments:`, payments);
                return { ...receivable, Payments: payments };
              } catch (error) {
                console.warn(`⚠️ Failed to fetch payments for ${receivable.invoice_number}:`, error);
                return { ...receivable, Payments: [] };
              }
            })
          );
          
          // Calculate totals
          const totalAmount = receivablesWithPayments.reduce((sum, r) => sum + parseFloat(r.amount || 0), 0);
          const receivedAmount = receivablesWithPayments.reduce((sum, r) => {
            if (r.Payments && Array.isArray(r.Payments)) {
              const paymentsTotal = r.Payments.reduce((paySum, payment) => paySum + parseFloat(payment.amount || 0), 0);
              return sum + paymentsTotal;
            }
            return sum;
          }, 0);
          const outstandingAmount = totalAmount - receivedAmount;
          
          console.log('📊 Receivables Summary - Total:', totalAmount, 'Received:', receivedAmount, 'Outstanding:', outstandingAmount);
          
          setReceivablesSummary({
            receivables: {
              count: receivablesWithPayments.length,
              total_amount: totalAmount,
              received_amount: receivedAmount,
              outstanding_amount: outstandingAmount
            }
          });
          console.log('✅ Receivables loaded with payments');
        }
      } catch (recError) {
        console.warn('⚠️ Receivables fetch failed:', recError.message);
        setReceivablesSummary(null);
      }

      // Fetch all purchase requests (same as GMD)
      try {
        const requestsRes = await purchaseRequestService.getAllRequests({
          show_all: true,
          limit: 1000
        });
        console.log('📊 Requests Response:', requestsRes);
        if (requestsRes?.data?.success) {
          const requests = requestsRes.data.data.requests || [];
          setAllRequests(requests);
          console.log('✅ Purchase Requests loaded:', requests.length, 'items');
          if (requests.length > 0) {
            console.log('First request sample:', requests[0]);
          }
        }
      } catch (reqError) {
        console.warn('⚠️ Requests fetch failed:', reqError.message);
        setAllRequests([]);
      }

      // Fetch all transactions
      try {
        const transactionsRes = await financeService.getTransactions({ limit: 100 });
        console.log('📊 Transactions Response:', transactionsRes);
        if (transactionsRes?.data?.success) {
          const transactions = transactionsRes.data.data.transactions || [];
          setAllTransactions(transactions);
          console.log('✅ Transactions loaded:', transactions.length, 'items');
        }
      } catch (txnError) {
        console.warn('⚠️ Transactions fetch failed:', txnError.message);
        setAllTransactions([]);
      }
    } catch (error) {
      console.error('❌ Failed to fetch receivables and requests:', error);
    } finally {
      setDataLoading(false);
      console.log('📊 Final State:', {
        requests: allRequests.length,
        transactions: allTransactions.length,
        receivables: receivablesSummary
      });
    }
  };

  // const fetchSubsidiaries = async () => {
  //   try {
  //     const response = await financeService.getSubsidiaries();
  //     if (response.data.success && Array.isArray(response.data.data.subsidiaries)) {
  //       setSubsidiaries(response.data.data.subsidiaries);
  //     } else {
  //       setSubsidiaries([]);
  //     }
  //   } catch (error) {
  //     console.error('Error fetching subsidiaries:', error);
  //     setSubsidiaries([]);
  //   }
  // };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch from new finance aggregation endpoint
      const params = new URLSearchParams();
      if (selectedSubsidiary) {
        params.append("subsidiary", selectedSubsidiary);
      }

      // Set date range based on period
      const now = new Date();
      let dateFrom = new Date();
      switch (selectedPeriod) {
        case "week":
          dateFrom.setDate(dateFrom.getDate() - 7);
          break;
        case "month":
          dateFrom.setMonth(dateFrom.getMonth() - 1);
          break;
        case "quarter":
          dateFrom.setMonth(dateFrom.getMonth() - 3);
          break;
        case "year":
          dateFrom.setFullYear(dateFrom.getFullYear() - 1);
          break;
        default:
          dateFrom.setMonth(dateFrom.getMonth() - 1);
      }

      params.append("date_from", dateFrom.toISOString().split("T")[0]);
      params.append("date_to", now.toISOString().split("T")[0]);

      const response = await api.get("/finance-aggregation/dashboard", {
        params: Object.fromEntries(params),
      });

      const data = response.data;

      // Debug logging
      console.log(
        "📊 Frontend received data for subsidiary:",
        selectedSubsidiary || "ALL",
      );
      console.log("   Total Revenue:", data.data?.overview?.totalRevenue);
      console.log(
        "   Purchase Requests:",
        data.data?.purchaseRequests?.totalRequests,
      );
      console.log("   Budgets:", data.data?.budgets?.length);

      if (data.success) {
        // Map aggregated data to dashboard format
        const aggregatedData = data.data || {};

        // Helper function to safely parse numbers
        const safeParseFloat = (value) => {
          const parsed = parseFloat(value);
          return isNaN(parsed) ? 0 : parsed;
        };

        const safeData = {
          metrics: {
            totalAssets: safeParseFloat(
              aggregatedData.overview?.inventoryValue || 0,
            ),
            totalLiabilities: 0,
            netWorth: safeParseFloat(aggregatedData.overview?.netProfit || 0),
            totalRevenue: safeParseFloat(
              aggregatedData.overview?.totalRevenue || 0,
            ),
            totalExpenses: safeParseFloat(
              aggregatedData.overview?.totalExpenses || 0,
            ),
            netIncome: safeParseFloat(aggregatedData.overview?.netProfit || 0),
            cashBalance:
              safeParseFloat(aggregatedData.overview?.totalRevenue || 0) -
              safeParseFloat(aggregatedData.overview?.totalExpenses || 0),
            pendingTransactions:
              (aggregatedData.purchaseRequests?.pending || 0) +
              (aggregatedData.expenses?.pending || 0),
          },
          recentTransactions: Array.isArray(aggregatedData.recentTransactions)
            ? aggregatedData.recentTransactions
            : [],
          budgetData: Array.isArray(aggregatedData.budgets)
            ? aggregatedData.budgets
            : [],
          period: { type: selectedPeriod },
          sales: {
            totalTransactions: aggregatedData.sales?.totalTransactions || 0,
            totalRevenue: safeParseFloat(
              aggregatedData.sales?.totalRevenue || 0,
            ),
            totalCOGS: safeParseFloat(aggregatedData.sales?.totalCOGS || 0),
            grossMargin: safeParseFloat(aggregatedData.sales?.grossMargin || 0),
          },
          purchaseRequests: {
            totalRequests: aggregatedData.purchaseRequests?.totalRequests || 0,
            totalAmount: safeParseFloat(
              aggregatedData.purchaseRequests?.totalAmount || 0,
            ),
            pending: aggregatedData.purchaseRequests?.pending || 0,
          },
          expenses: {
            totalClaims: aggregatedData.expenses?.totalClaims || 0,
            totalAmount: safeParseFloat(
              aggregatedData.expenses?.totalAmount || 0,
            ),
            pending: aggregatedData.expenses?.pending || 0,
          },
        };

        setDashboardData(safeData);

        // Check if there's any meaningful data
        // When filtering by subsidiary, always show the UI (even if zeros)
        const hasAnyData = selectedSubsidiary
          ? true // Always show UI when filtering by subsidiary
          : safeData.metrics.totalRevenue > 0 ||
            safeData.metrics.totalExpenses > 0 ||
            safeData.budgetData.length > 0 ||
            safeData.sales.totalRevenue > 0 ||
            safeData.purchaseRequests.totalRequests > 0 ||
            safeData.expenses.totalClaims > 0;

        setHasData(hasAnyData);
      } else {
        setError(data.message || "Failed to load dashboard data");
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      setError(
        error.response?.data?.message ||
          "Failed to load dashboard data. Please try again.",
      );
      // Set empty data on error to prevent crashes
      setDashboardData({
        metrics: {
          totalAssets: 0,
          totalLiabilities: 0,
          netWorth: 0,
          totalRevenue: 0,
          totalExpenses: 0,
          netIncome: 0,
          cashBalance: 0,
          pendingTransactions: 0,
        },
        recentTransactions: [],
        budgetData: [],
        period: { type: selectedPeriod },
      });
      setHasData(false);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount, currency = "NGN") => {
    const safeAmount =
      typeof amount === "number" && !isNaN(amount) ? amount : 0;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(safeAmount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      return "Invalid Date";
    }
  };

  const getMetricChange = (current, previous) => {
    if (!previous || previous === 0) return { percentage: 0, isPositive: true };
    const change = ((current - previous) / previous) * 100;
    return {
      percentage: Math.abs(change).toFixed(1),
      isPositive: change >= 0,
    };
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading dashboard...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Finance Dashboard
            </h1>
            <p className="text-gray-600 mt-1">
              Financial overview and key metrics
            </p>
          </div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <AlertCircle className="h-6 w-6 text-red-400 mr-3" />
            <h3 className="text-lg font-semibold text-red-800">
              Error Loading Dashboard
            </h3>
          </div>
          <p className="text-red-700 mb-4">{error}</p>
          <button
            onClick={fetchDashboardData}
            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Show empty state if no data
  if (!loading && !hasData) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Finance Dashboard
            </h1>
            <p className="text-gray-600 mt-1">
              Financial overview and key metrics
            </p>
          </div>
          <button
            onClick={fetchDashboardData}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
          >
            <Activity className="h-4 w-4" />
            <span>Refresh</span>
          </button>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-12 text-center">
          <BarChart3 className="h-16 w-16 text-blue-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            No Financial Data Yet
          </h3>
          <p className="text-gray-600 mb-6">
            Start by creating accounts and recording transactions to see your
            financial overview.
          </p>
          <div className="flex justify-center space-x-4">
            <button className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors">
              Create Account
            </button>
            <button className="bg-white text-blue-600 border border-blue-600 px-6 py-2 rounded-lg hover:bg-blue-50 transition-colors">
              Record Transaction
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Finance Dashboard
          </h1>
          <p className="text-gray-600 mt-1">
            Financial overview and key metrics
          </p>
          {selectedSubsidiary && (
            <p className="text-sm text-amber-600 mt-1">
              ℹ️ Note: POS/Sales data is not included in subsidiary filtering
            </p>
          )}
        </div>
        <div className="flex items-center space-x-4">
          <select
            value={selectedSubsidiary}
            onChange={(e) => setSelectedSubsidiary(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">All Subsidiaries</option>
            {Array.isArray(SUBSIDIARIES) &&
              SUBSIDIARIES.map((subsidiary) => (
                <option key={subsidiary.value} value={subsidiary.value}>
                  {subsidiary.label}
                </option>
              ))}
          </select>
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="quarter">This Quarter</option>
            <option value="year">This Year</option>
          </select>
          <button
            onClick={fetchDashboardData}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
          >
            <Activity className="h-4 w-4" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Financial Overview - Income vs Expenses */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Total Expenses - Clickable */}
        <div 
          onClick={() => setShowExpensesModal(true)}
          className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl shadow-sm border-2 border-red-200 p-6 hover:shadow-lg hover:scale-[1.02] transition-all cursor-pointer group"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <div className="bg-red-500 p-2 rounded-lg group-hover:bg-red-600 transition-colors">
                <TrendingDown className="h-5 w-5 text-white" />
              </div>
              <h3 className="text-sm font-semibold text-red-900">Total Expenses</h3>
            </div>
            <span className="text-xs text-red-600 font-medium bg-red-200 px-2 py-1 rounded">Click to view</span>
          </div>
          <p className="text-3xl font-bold text-red-900 mb-2">
            {formatCurrency(
              allRequests.reduce((sum, req) => sum + (Number(req.amount) || 0), 0) +
              allTransactions.filter(t => t.status === 'approved' || t.status === 'posted').reduce((sum, txn) => sum + (Number(txn.amount) || 0), 0)
            )}
          </p>
          <div className="flex items-center gap-2 text-xs text-red-700">
            <span>{allRequests.length} requests</span>
            {allTransactions.length > 0 && (
              <>
                <span>•</span>
                <span>{allTransactions.filter(t => t.status === 'approved' || t.status === 'posted').length} transactions</span>
              </>
            )}
          </div>
        </div>

        {/* Total Income - Clickable */}
        <div 
          onClick={() => navigate('/finance/receivables')}
          className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl shadow-sm border-2 border-green-200 p-6 hover:shadow-lg hover:scale-[1.02] transition-all cursor-pointer group"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <div className="bg-green-500 p-2 rounded-lg group-hover:bg-green-600 transition-colors">
                <TrendingUp className="h-5 w-5 text-white" />
              </div>
              <h3 className="text-sm font-semibold text-green-900">Total Income</h3>
            </div>
            <span className="text-xs text-green-600 font-medium bg-green-200 px-2 py-1 rounded">Click to view</span>
          </div>
          <p className="text-3xl font-bold text-green-900 mb-2">
            {formatCurrency(receivablesSummary?.receivables?.received_amount || 0)}
          </p>
          <div className="flex items-center gap-2 text-xs text-green-700 mb-3">
            <span>{receivablesSummary?.receivables?.count || 0} receivables</span>
            <span>•</span>
            <span>Actual received</span>
          </div>
          {(receivablesSummary?.receivables?.outstanding_amount || 0) > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate('/finance/receivables');
              }}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold py-2 px-3 rounded-lg transition-colors flex items-center justify-center gap-1.5"
            >
              <AlertCircle className="h-3.5 w-3.5" />
              Outstanding: {formatCurrency(receivablesSummary?.receivables?.outstanding_amount || 0)}
            </button>
          )}
        </div>

        {/* Net Position - Clickable */}
        <div 
          className={`bg-gradient-to-br ${
            (receivablesSummary?.receivables?.received_amount || 0) >= 
            (allRequests.reduce((sum, req) => sum + (Number(req.amount) || 0), 0) +
            allTransactions.filter(t => t.status === 'approved' || t.status === 'posted').reduce((sum, txn) => sum + (Number(txn.amount) || 0), 0))
            ? 'from-blue-50 to-blue-100 border-blue-200' 
            : 'from-orange-50 to-orange-100 border-orange-200'
          } rounded-xl shadow-sm border-2 p-6 hover:shadow-lg hover:scale-[1.02] transition-all cursor-pointer group`}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <div className={`${
                (receivablesSummary?.receivables?.received_amount || 0) >= 
                (allRequests.reduce((sum, req) => sum + (Number(req.amount) || 0), 0) +
                allTransactions.filter(t => t.status === 'approved' || t.status === 'posted').reduce((sum, txn) => sum + (Number(txn.amount) || 0), 0))
                ? 'bg-blue-500 group-hover:bg-blue-600' 
                : 'bg-orange-500 group-hover:bg-orange-600'
              } p-2 rounded-lg transition-colors`}>
                <BarChart3 className="h-5 w-5 text-white" />
              </div>
              <h3 className={`text-sm font-semibold ${
                (receivablesSummary?.receivables?.received_amount || 0) >= 
                (allRequests.reduce((sum, req) => sum + (Number(req.amount) || 0), 0) +
                allTransactions.filter(t => t.status === 'approved' || t.status === 'posted').reduce((sum, txn) => sum + (Number(txn.amount) || 0), 0))
                ? 'text-blue-900' 
                : 'text-orange-900'
              }`}>Net Position</h3>
            </div>
            <span className={`text-xs font-medium px-2 py-1 rounded ${
              (receivablesSummary?.receivables?.received_amount || 0) >= 
              (allRequests.reduce((sum, req) => sum + (Number(req.amount) || 0), 0) +
              allTransactions.filter(t => t.status === 'approved' || t.status === 'posted').reduce((sum, txn) => sum + (Number(txn.amount) || 0), 0))
              ? 'text-blue-600 bg-blue-200' 
              : 'text-orange-600 bg-orange-200'
            }`}>View</span>
          </div>
          <p className={`text-3xl font-bold mb-2 ${
            (receivablesSummary?.receivables?.received_amount || 0) >= 
            (allRequests.reduce((sum, req) => sum + (Number(req.amount) || 0), 0) +
            allTransactions.filter(t => t.status === 'approved' || t.status === 'posted').reduce((sum, txn) => sum + (Number(txn.amount) || 0), 0))
            ? 'text-blue-900' 
            : 'text-orange-900'
          }`}>
            {formatCurrency(
              (receivablesSummary?.receivables?.received_amount || 0) -
              (allRequests.reduce((sum, req) => sum + (Number(req.amount) || 0), 0) +
              allTransactions.filter(t => t.status === 'approved' || t.status === 'posted').reduce((sum, txn) => sum + (Number(txn.amount) || 0), 0))
            )}
          </p>
          <p className={`text-xs ${
            (receivablesSummary?.receivables?.received_amount || 0) >= 
            (allRequests.reduce((sum, req) => sum + (Number(req.amount) || 0), 0) +
            allTransactions.filter(t => t.status === 'approved' || t.status === 'posted').reduce((sum, txn) => sum + (Number(txn.amount) || 0), 0))
            ? 'text-blue-700' 
            : 'text-orange-700'
          }`}>
            {(receivablesSummary?.receivables?.received_amount || 0) >= 
            (allRequests.reduce((sum, req) => sum + (Number(req.amount) || 0), 0) +
            allTransactions.filter(t => t.status === 'approved' || t.status === 'posted').reduce((sum, txn) => sum + (Number(txn.amount) || 0), 0))
            ? 'Income exceeds expenses' 
            : 'Expenses exceed income'}
          </p>
        </div>
      </div>

      {/* Enhanced Financial Chart with Date Range */}
      <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl border border-gray-200 shadow-md p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-purple-600" />
              Financial Performance
            </h3>
            <p className="text-sm text-gray-500 mt-1">Income vs Expenses Analysis</p>
          </div>
          
          {/* Date Range Selector */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-gray-500" />
              <input
                type="date"
                value={dateRange.from}
                onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
              <span className="text-gray-500">to</span>
              <input
                type="date"
                value={dateRange.to}
                onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <span className={`text-sm font-semibold px-3 py-1.5 rounded-lg ${
              (receivablesSummary?.receivables?.total_amount || 0) >= 
              (allRequests.reduce((sum, req) => sum + (Number(req.amount) || 0), 0) +
              allTransactions.filter(t => t.status === 'approved' || t.status === 'posted').reduce((sum, txn) => sum + (Number(txn.amount) || 0), 0))
              ? 'bg-green-100 text-green-700' 
              : 'bg-orange-100 text-orange-700'
            }`}>
              {(receivablesSummary?.receivables?.total_amount || 0) >= 
              (allRequests.reduce((sum, req) => sum + (Number(req.amount) || 0), 0) +
              allTransactions.filter(t => t.status === 'approved' || t.status === 'posted').reduce((sum, txn) => sum + (Number(txn.amount) || 0), 0))
              ? '✓ Healthy' : '⚠ Warning'}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Chart Section */}
          <div className="lg:col-span-2 space-y-5">
            {/* Income Bar */}
            <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-sm font-semibold text-gray-700">Income</span>
                </div>
                <span className="text-lg font-bold text-green-600">
                  {formatCurrency(receivablesSummary?.receivables?.received_amount || 0)}
                </span>
              </div>
              <div className="relative h-8 bg-gray-100 rounded-lg overflow-hidden">
                <div 
                  className="absolute inset-y-0 left-0 bg-gradient-to-r from-green-400 to-green-600 rounded-lg transition-all duration-1000 ease-out flex items-center justify-end px-3"
                  style={{ 
                    width: `${Math.max(10, Math.min(100, ((receivablesSummary?.receivables?.received_amount || 0) / 
                      Math.max(
                        (receivablesSummary?.receivables?.received_amount || 1),
                        (allRequests.reduce((sum, req) => sum + (Number(req.amount) || 0), 0) +
                        allTransactions.filter(t => t.status === 'approved' || t.status === 'posted').reduce((sum, txn) => sum + (Number(txn.amount) || 0), 0))
                      )) * 100))}%` 
                  }}
                >
                  <span className="text-xs font-bold text-white">
                    {Math.round(((receivablesSummary?.receivables?.received_amount || 0) / 
                      Math.max(
                        (receivablesSummary?.receivables?.received_amount || 1),
                        (allRequests.reduce((sum, req) => sum + (Number(req.amount) || 0), 0) +
                        allTransactions.filter(t => t.status === 'approved' || t.status === 'posted').reduce((sum, txn) => sum + (Number(txn.amount) || 0), 0))
                      )) * 100)}%
                  </span>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">Actual received from {receivablesSummary?.receivables?.count || 0} receivables</p>
            </div>

            {/* Expenses Bar */}
            <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  <span className="text-sm font-semibold text-gray-700">Expenses</span>
                </div>
                <span className="text-lg font-bold text-red-600">
                  {formatCurrency(
                    allRequests.reduce((sum, req) => sum + (Number(req.amount) || 0), 0) +
                    allTransactions.filter(t => t.status === 'approved' || t.status === 'posted').reduce((sum, txn) => sum + (Number(txn.amount) || 0), 0)
                  )}
                </span>
              </div>
              <div className="relative h-8 bg-gray-100 rounded-lg overflow-hidden">
                <div 
                  className="absolute inset-y-0 left-0 bg-gradient-to-r from-red-400 to-red-600 rounded-lg transition-all duration-1000 ease-out flex items-center justify-end px-3"
                  style={{ 
                    width: `${Math.max(10, Math.min(100, ((allRequests.reduce((sum, req) => sum + (Number(req.amount) || 0), 0) +
                      allTransactions.filter(t => t.status === 'approved' || t.status === 'posted').reduce((sum, txn) => sum + (Number(txn.amount) || 0), 0)) / 
                      Math.max(
                        (receivablesSummary?.receivables?.total_amount || 1),
                        (allRequests.reduce((sum, req) => sum + (Number(req.amount) || 0), 0) +
                        allTransactions.filter(t => t.status === 'approved' || t.status === 'posted').reduce((sum, txn) => sum + (Number(txn.amount) || 0), 0))
                      )) * 100))}%` 
                  }}
                >
                  <span className="text-xs font-bold text-white">
                    {Math.round(((allRequests.reduce((sum, req) => sum + (Number(req.amount) || 0), 0) +
                      allTransactions.filter(t => t.status === 'approved' || t.status === 'posted').reduce((sum, txn) => sum + (Number(txn.amount) || 0), 0)) / 
                      Math.max(
                        (receivablesSummary?.receivables?.total_amount || 1),
                        (allRequests.reduce((sum, req) => sum + (Number(req.amount) || 0), 0) +
                        allTransactions.filter(t => t.status === 'approved' || t.status === 'posted').reduce((sum, txn) => sum + (Number(txn.amount) || 0), 0))
                      )) * 100)}%
                  </span>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {allRequests.length + allTransactions.filter(t => t.status === 'approved' || t.status === 'posted').length} items
              </p>
            </div>

            {/* Net Position */}
            <div className={`rounded-lg p-4 shadow-sm border-2 ${
              (receivablesSummary?.receivables?.received_amount || 0) >= 
              (allRequests.reduce((sum, req) => sum + (Number(req.amount) || 0), 0) +
              allTransactions.filter(t => t.status === 'approved' || t.status === 'posted').reduce((sum, txn) => sum + (Number(txn.amount) || 0), 0))
              ? 'bg-green-50 border-green-200' 
              : 'bg-orange-50 border-orange-200'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${
                    (receivablesSummary?.receivables?.received_amount || 0) >= 
                    (allRequests.reduce((sum, req) => sum + (Number(req.amount) || 0), 0) +
                    allTransactions.filter(t => t.status === 'approved' || t.status === 'posted').reduce((sum, txn) => sum + (Number(txn.amount) || 0), 0))
                    ? 'bg-green-500' : 'bg-orange-500'
                  }`}></div>
                  <span className="text-sm font-semibold text-gray-700">Net Position</span>
                </div>
                <span className={`text-xl font-bold ${
                  (receivablesSummary?.receivables?.received_amount || 0) >= 
                  (allRequests.reduce((sum, req) => sum + (Number(req.amount) || 0), 0) +
                  allTransactions.filter(t => t.status === 'approved' || t.status === 'posted').reduce((sum, txn) => sum + (Number(txn.amount) || 0), 0))
                  ? 'text-green-600' : 'text-orange-600'
                }`}>
                  {formatCurrency(
                    (receivablesSummary?.receivables?.received_amount || 0) -
                    (allRequests.reduce((sum, req) => sum + (Number(req.amount) || 0), 0) +
                    allTransactions.filter(t => t.status === 'approved' || t.status === 'posted').reduce((sum, txn) => sum + (Number(txn.amount) || 0), 0))
                  )}
                </span>
              </div>
            </div>
          </div>

          {/* Stats Summary */}
          <div className="space-y-4">
            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-5 text-white shadow-lg">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold">Income</h4>
                <TrendingUp className="h-5 w-5" />
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between pb-2 border-b border-white/20">
                  <span className="text-white/80">Receivables</span>
                  <span className="font-bold">{receivablesSummary?.receivables?.count || 0}</span>
                </div>
                <div className="flex justify-between pb-2 border-b border-white/20">
                  <span className="text-white/80">Received</span>
                  <span className="font-semibold text-sm">
                    {formatCurrency(receivablesSummary?.receivables?.received_amount || 0)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/80">Outstanding</span>
                  <span className="font-semibold text-sm">
                    {formatCurrency(receivablesSummary?.receivables?.outstanding_amount || 0)}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-lg p-5 text-white shadow-lg">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold">Expenses</h4>
                <TrendingDown className="h-5 w-5" />
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between pb-2 border-b border-white/20">
                  <span className="text-white/80">Requests</span>
                  <span className="font-bold">{allRequests.length}</span>
                </div>
                <div className="flex justify-between pb-2 border-b border-white/20">
                  <span className="text-white/80">Transactions</span>
                  <span className="font-bold">{allTransactions.filter(t => t.status === 'approved' || t.status === 'posted').length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/80">Total</span>
                  <span className="font-semibold text-sm">
                    {formatCurrency(
                      allRequests.reduce((sum, req) => sum + (Number(req.amount) || 0), 0) +
                      allTransactions.filter(t => t.status === 'approved' || t.status === 'posted').reduce((sum, txn) => sum + (Number(txn.amount) || 0), 0)
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Key Financial Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Total Assets */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Assets</p>
              <p className="text-3xl font-bold text-green-600">
                {formatCurrency(dashboardData.metrics.totalAssets)}
              </p>
              <div className="flex items-center mt-2">
                <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
                <span className="text-sm text-green-600">Strong position</span>
              </div>
            </div>
            <Building className="h-8 w-8 text-green-600" />
          </div>
        </div>

        {/* Net Worth */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Net Worth</p>
              <p className="text-3xl font-bold text-blue-600">
                {formatCurrency(dashboardData.metrics.netWorth)}
              </p>
              <div className="flex items-center mt-2">
                <TrendingUp className="h-4 w-4 text-blue-500 mr-1" />
                <span className="text-sm text-blue-600">Equity growth</span>
              </div>
            </div>
            <Wallet className="h-8 w-8 text-blue-600" />
          </div>
        </div>

        {/* Cash Balance */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Cash Balance</p>
              <p className="text-3xl font-bold text-purple-600">
                {formatCurrency(dashboardData.metrics.cashBalance)}
              </p>
              <div className="flex items-center mt-2">
                <CreditCard className="h-4 w-4 text-purple-500 mr-1" />
                <span className="text-sm text-purple-600">Available funds</span>
              </div>
            </div>
            <Banknote className="h-8 w-8 text-purple-600" />
          </div>
        </div>

        {/* Net Income */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Net Income</p>
              <p
                className={`text-3xl font-bold ${dashboardData.metrics.netIncome >= 0 ? "text-green-600" : "text-red-600"}`}
              >
                {formatCurrency(dashboardData.metrics.netIncome)}
              </p>
              <div className="flex items-center mt-2">
                {dashboardData.metrics.netIncome >= 0 ? (
                  <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
                ) : (
                  <TrendingDown className="h-4 w-4 text-red-500 mr-1" />
                )}
                <span
                  className={`text-sm ${dashboardData.metrics.netIncome >= 0 ? "text-green-600" : "text-red-600"}`}
                >
                  {selectedPeriod} performance
                </span>
              </div>
            </div>
            <BarChart3
              className={`h-8 w-8 ${dashboardData.metrics.netIncome >= 0 ? "text-green-600" : "text-red-600"}`}
            />
          </div>
        </div>
      </div>

      {/* Revenue vs Expenses */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sales Overview - NEW */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">
              Sales Overview (POS/Inventory)
            </h3>
            <TrendingUp className="h-5 w-5 text-green-600" />
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <Banknote className="h-4 w-4 text-green-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">
                    Total Sales Revenue
                  </p>
                  <p className="text-sm text-gray-500">
                    {dashboardData.sales?.totalTransactions || 0} transactions
                  </p>
                </div>
              </div>
              <span className="text-xl font-bold text-green-600">
                {formatCurrency(
                  parseFloat(dashboardData.sales?.totalRevenue || 0),
                )}
              </span>
            </div>

            <div className="flex items-center justify-between p-4 bg-orange-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                  <TrendingDown className="h-4 w-4 text-orange-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">
                    Cost of Goods Sold
                  </p>
                  <p className="text-sm text-gray-500">Inventory costs</p>
                </div>
              </div>
              <span className="text-xl font-bold text-orange-600">
                {formatCurrency(
                  parseFloat(dashboardData.sales?.totalCOGS || 0),
                )}
              </span>
            </div>

            {/* Gross Margin */}
            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-900">Gross Margin</span>
                <span className="text-lg font-bold text-blue-600">
                  {(
                    parseFloat(dashboardData.sales?.grossMargin || 0) || 0
                  ).toFixed(1)}
                  %
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">
              Revenue vs Expenses
            </h3>
            <PieChart className="h-5 w-5 text-blue-600" />
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <TrendingUp className="h-4 w-4 text-green-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Total Revenue</p>
                  <p className="text-sm text-gray-500">
                    {selectedPeriod} period
                  </p>
                </div>
              </div>
              <span className="text-xl font-bold text-green-600">
                {formatCurrency(dashboardData.metrics.totalRevenue)}
              </span>
            </div>

            <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                  <TrendingDown className="h-4 w-4 text-red-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Total Expenses</p>
                  <p className="text-sm text-gray-500">
                    {selectedPeriod} period
                  </p>
                </div>
              </div>
              <span className="text-xl font-bold text-red-600">
                {formatCurrency(dashboardData.metrics.totalExpenses)}
              </span>
            </div>

            {/* Profit Margin */}
            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-900">Profit Margin</span>
                <span className="text-lg font-bold text-blue-600">
                  {dashboardData.metrics.totalRevenue > 0
                    ? (
                        (dashboardData.metrics.netIncome /
                          dashboardData.metrics.totalRevenue) *
                        100
                      ).toFixed(1)
                    : 0}
                  %
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Purchase Requests & Expenses Summary - NEW */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">
              Purchase Requests
            </h3>
            <FileText className="h-5 w-5 text-blue-600" />
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">Total Requests</p>
                <p className="text-sm text-gray-500">{selectedPeriod} period</p>
              </div>
              <span className="text-2xl font-bold text-blue-600">
                {dashboardData.purchaseRequests?.totalRequests || 0}
              </span>
            </div>

            <div className="flex items-center justify-between p-4 bg-orange-50 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">
                  Pending Finance Approval
                </p>
                <p className="text-sm text-gray-500">Requires action</p>
              </div>
              <span className="text-2xl font-bold text-orange-600">
                {dashboardData.purchaseRequests?.pending || 0}
              </span>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">Total Amount</p>
                <p className="text-sm text-gray-500">All requests</p>
              </div>
              <span className="text-xl font-bold text-gray-900">
                {formatCurrency(
                  dashboardData.purchaseRequests?.totalAmount || 0,
                )}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">HR Expenses</h3>
            <CreditCard className="h-5 w-5 text-purple-600" />
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">Total Claims</p>
                <p className="text-sm text-gray-500">{selectedPeriod} period</p>
              </div>
              <span className="text-2xl font-bold text-purple-600">
                {dashboardData.expenses?.totalClaims || 0}
              </span>
            </div>

            <div className="flex items-center justify-between p-4 bg-orange-50 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">
                  Pending Finance Approval
                </p>
                <p className="text-sm text-gray-500">Requires action</p>
              </div>
              <span className="text-2xl font-bold text-orange-600">
                {dashboardData.expenses?.pending || 0}
              </span>
            </div>

            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">Total Amount</p>
                <p className="text-sm text-gray-500">All claims</p>
              </div>
              <span className="text-xl font-bold text-gray-900">
                {formatCurrency(dashboardData.expenses?.totalAmount || 0)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Revenue vs Expenses - REMOVED DUPLICATE */}

      {/* Pending Transactions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">
            Pending Transactions
          </h3>
          <Clock className="h-5 w-5 text-orange-600" />
        </div>

        {dashboardData.metrics.pendingTransactions > 0 ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-orange-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                  <Clock className="h-4 w-4 text-orange-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Awaiting Approval</p>
                  <p className="text-sm text-gray-500">Requires attention</p>
                </div>
              </div>
              <span className="text-2xl font-bold text-orange-600">
                {dashboardData.metrics.pendingTransactions}
              </span>
            </div>

            <button className="w-full bg-orange-600 text-white py-2 px-4 rounded-lg hover:bg-orange-700 transition-colors">
              Review Pending Transactions
            </button>
          </div>
        ) : (
          <div className="text-center py-8">
            <CheckCircle className="h-12 w-12 text-green-300 mx-auto mb-3" />
            <p className="text-gray-500">All transactions are up to date</p>
          </div>
        )}
      </div>
      {/* </div> */}

      {/* Recent Transactions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">
            Recent Transactions
          </h3>
          <FileText className="h-5 w-5 text-blue-600" />
        </div>

        {Array.isArray(dashboardData.recentTransactions) &&
        dashboardData.recentTransactions.length === 0 ? (
          <div className="text-center py-8">
            <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No recent transactions</p>
          </div>
        ) : (
          <div className="space-y-4">
            {Array.isArray(dashboardData.recentTransactions) &&
              dashboardData.recentTransactions
                .slice(0, 5)
                .map((transaction) => (
                  <div
                    key={transaction.id}
                    className="flex items-center justify-between p-4 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <FileText className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">
                          {transaction.description || "No description"}
                        </p>
                        <p className="text-sm text-gray-500">
                          {transaction.DebitAccount?.name || "N/A"} →{" "}
                          {transaction.CreditAccount?.name || "N/A"}
                        </p>
                        <p className="text-xs text-gray-400">
                          {formatDate(transaction.date)}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-gray-900">
                        {formatCurrency(transaction.amount || 0)}
                      </p>
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          transaction.status === "posted"
                            ? "bg-green-100 text-green-800"
                            : transaction.status === "approved"
                              ? "bg-blue-100 text-blue-800"
                              : transaction.status === "pending"
                                ? "bg-yellow-100 text-yellow-800"
                                : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {transaction.status || "unknown"}
                      </span>
                    </div>
                  </div>
                ))}
          </div>
        )}
      </div>

      {/* Budget Overview */}
      {Array.isArray(dashboardData.budgetData) &&
        dashboardData.budgetData.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">
                Budget Overview
              </h3>
              <Target className="h-5 w-5 text-blue-600" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {dashboardData.budgetData.slice(0, 3).map((budget) => {
                const budgetedAmount = parseFloat(budget.budgeted || 0);
                const actualAmount = parseFloat(budget.actual || 0);
                const percentageUsed =
                  budgetedAmount > 0
                    ? (actualAmount / budgetedAmount) * 100
                    : 0;
                const remaining = parseFloat(budget.available ?? (budgetedAmount - actualAmount));

                return (
                  <div
                    key={budget.id}
                    className="p-4 border border-gray-200 rounded-lg"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-gray-900">
                        {budget.name || "Unnamed Budget"}
                      </h4>
                      <span className="text-sm text-gray-500">
                        {budget.type || "N/A"}
                      </span>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Budgeted:</span>
                        <span className="font-medium">
                          {formatCurrency(budgetedAmount)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Actual:</span>
                        <span className="font-medium">
                          {formatCurrency(actualAmount)}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full"
                          style={{
                            width: `${Math.min(percentageUsed, 100)}%`,
                          }}
                        ></div>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-500">
                          {percentageUsed.toFixed(1)}% used
                        </span>
                        <span className="text-gray-500">
                          {formatCurrency(remaining)} remaining
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

      {/* Quick Actions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">
          Quick Actions
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button
            onClick={() => navigate("/finance/transactions/new")}
            className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-center"
          >
            <FileText className="h-6 w-6 text-blue-600 mx-auto mb-2" />
            <span className="text-sm font-medium text-gray-900">
              New Transaction
            </span>
          </button>

          <button
            onClick={() => navigate("/finance/accounts")}
            className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-center"
          >
            <Building className="h-6 w-6 text-green-600 mx-auto mb-2" />
            <span className="text-sm font-medium text-gray-900">
              Manage Accounts
            </span>
          </button>

          <button
            onClick={() => navigate("/finance/budgets")}
            className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-center"
          >
            <Target className="h-6 w-6 text-purple-600 mx-auto mb-2" />
            <span className="text-sm font-medium text-gray-900">
              Budget Planning
            </span>
          </button>

          <button
            onClick={() => navigate("/finance/reports")}
            className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-center"
          >
            <BarChart3 className="h-6 w-6 text-orange-600 mx-auto mb-2" />
            <span className="text-sm font-medium text-gray-900">
              Financial Reports
            </span>
          </button>
        </div>
      </div>

      {/* Expenses Modal */}
      {showExpensesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-red-600 to-red-700 p-6 flex items-center justify-between">
              <div>
                <h3 className="text-2xl font-bold text-white flex items-center gap-2">
                  <TrendingDown className="h-6 w-6" />
                  Total Expenses Breakdown
                </h3>
                <p className="text-red-100 mt-1">
                  {allRequests.length + allTransactions.filter(t => t.status === 'approved' || t.status === 'posted').length} items • Total: {formatCurrency(
                    allRequests.reduce((sum, req) => sum + (Number(req.amount) || 0), 0) +
                    allTransactions.filter(t => t.status === 'approved' || t.status === 'posted').reduce((sum, txn) => sum + (Number(txn.amount) || 0), 0)
                  )}
                </p>
              </div>
              <button
                onClick={() => setShowExpensesModal(false)}
                className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6">
              {/* Purchase Requests Section */}
              <div className="mb-8">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <FileText className="h-5 w-5 text-purple-600" />
                  Purchase Requests ({allRequests.length})
                </h4>
                <div className="space-y-3">
                  {allRequests.map((req) => (
                    <div 
                      key={req.id} 
                      onClick={() => navigate(`/purchase-requests/${req.request_id}`)}
                      className="bg-gradient-to-br from-white to-purple-50 rounded-xl border-2 border-purple-200 p-4 hover:shadow-lg hover:border-purple-400 transition-all cursor-pointer group"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <span className="font-mono text-sm font-semibold text-purple-900">
                              {req.request_id}
                            </span>
                            <span className="text-xs text-purple-600 bg-purple-100 px-2 py-1 rounded group-hover:bg-purple-200 transition-colors">
                              Click to view details →
                            </span>
                          </div>
                          
                          {/* Status and Stage */}
                          <div className="grid grid-cols-2 gap-3 mb-2">
                            <div>
                              <p className="text-xs text-gray-500 mb-1">Status</p>
                              <span className={`inline-flex items-center text-xs px-2 py-1 rounded-full font-medium ${
                                req.status === 'completed' ? 'bg-green-100 text-green-800' :
                                req.status === 'payment_in_progress' ? 'bg-blue-100 text-blue-800' :
                                req.status === 'awaiting_payment_confirmation' ? 'bg-yellow-100 text-yellow-800' :
                                req.status === 'awaiting_delivery_confirmation' ? 'bg-teal-100 text-teal-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {req.status?.replace(/_/g, ' ')}
                              </span>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 mb-1">Current Stage</p>
                              <span className={`inline-flex items-center text-xs px-2 py-1 rounded-full font-medium ${
                                req.current_stage === 'completed' ? 'bg-green-100 text-green-800' :
                                req.current_stage === 'delivery_stage' ? 'bg-teal-100 text-teal-800' :
                                req.current_stage === 'pay_vendor_stage' ? 'bg-indigo-100 text-indigo-800' :
                                req.current_stage === 'confirm_payment_stage' ? 'bg-yellow-100 text-yellow-800' :
                                req.current_stage === 'finance_stage' ? 'bg-orange-100 text-orange-800' :
                                'bg-purple-100 text-purple-800'
                              }`}>
                                {req.current_stage?.replace(/_stage|_/g, ' ').trim()}
                              </span>
                            </div>
                          </div>

                          <div className="flex items-center gap-4 text-xs text-gray-600">
                            <span className="flex items-center gap-1">
                              <Building className="h-3 w-3" />
                              {req.SubsidiaryInfo?.name || req.subsidiary || 'N/A'}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {formatDate(req.created_at)}
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-purple-900">
                            {formatCurrency(req.amount)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                  {allRequests.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>No purchase requests</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Transactions Section */}
              {allTransactions.filter(t => t.status === 'approved' || t.status === 'posted').length > 0 && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Banknote className="h-5 w-5 text-blue-600" />
                    Transactions ({allTransactions.filter(t => t.status === 'approved' || t.status === 'posted').length})
                  </h4>
                  <div className="space-y-3">
                    {allTransactions.filter(t => t.status === 'approved' || t.status === 'posted').map((txn) => (
                      <div 
                        key={txn.id} 
                        onClick={() => navigate('/finance/transactions')}
                        className="bg-gradient-to-br from-white to-blue-50 rounded-xl border-2 border-blue-200 p-4 hover:shadow-lg hover:border-blue-400 transition-all cursor-pointer group"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-3">
                              <span className="font-mono text-sm font-semibold text-blue-900">
                                {txn.transaction_number}
                              </span>
                              <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded group-hover:bg-blue-200 transition-colors">
                                Click to view details →
                              </span>
                            </div>
                            
                            {/* Status and Type */}
                            <div className="grid grid-cols-2 gap-3 mb-2">
                              <div>
                                <p className="text-xs text-gray-500 mb-1">Status</p>
                                <span className={`inline-flex items-center text-xs px-2 py-1 rounded-full font-medium ${
                                  txn.status === 'approved' ? 'bg-green-100 text-green-800' :
                                  txn.status === 'pending_gmd_approval' ? 'bg-yellow-100 text-yellow-800' :
                                  txn.status === 'posted' ? 'bg-blue-100 text-blue-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {txn.status?.replace(/_/g, ' ')}
                                </span>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 mb-1">Type</p>
                                <span className="inline-flex items-center text-xs px-2 py-1 rounded-full font-medium bg-blue-100 text-blue-800 capitalize">
                                  {txn.type}
                                </span>
                              </div>
                            </div>

                            <div className="flex items-center gap-4 text-xs text-gray-600">
                              <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {formatDate(txn.date)}
                              </span>
                              {txn.payment_method && (
                                <span className="capitalize">{txn.payment_method}</span>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-blue-900">
                              {formatCurrency(txn.amount)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="border-t p-4 bg-gray-50">
              <button
                onClick={() => setShowExpensesModal(false)}
                className="w-full bg-gradient-to-r from-red-600 to-red-700 text-white py-3 rounded-lg hover:from-red-700 hover:to-red-800 transition-colors font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinanceDashboard;
